import { JSX, useCallback, useEffect, useMemo, useReducer, useState } from 'react';
import { arrayMove } from '@dnd-kit/sortable';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import { Grid } from '@mui/system';
import clsx from 'clsx';
import { isEmpty, isNil, toString, uniq } from 'lodash';
import { RESET, SYSTEM_COLLAPSE, SYSTEM_EXPAND, USER_EXPAND } from 'common/actions/row-groups/types';
import { rowsGroupsReducer } from 'common/reducers/rowsGroups';
import { RowsGroups } from 'common/reducers/rowsGroups/types';
import { useStore } from 'common/store';
import { ScalarSpreadsheetProps } from 'common/types/scalarSpreadsheet';
import { UseStoreValues } from 'common/types/store';
import { COLUMN_TYPES, Filter, SortKey, SummaryGroupBy } from 'common/types/summaryDefinitions';
import { CustomCompactContainer, CustomContainer, CustomScalarSpreadsheet, Tab } from 'components';
import { GridSkeleton } from 'components/Grid';
import useWorkbook from 'components/ScalarSpreadsheet/utilities/useWorkbook';
import WorkbookContext from 'components/ScalarSpreadsheet/utilities/WorkbookContext';
import ChartDataContext from 'components/WithChart/ChartDataContext';
import { useLayoutContext } from 'context/LayoutContext';
import { useSummaryDefinitions } from 'context/SummaryDefinitionsContext';
import { EXPORT_FIT_COLUMNS_ATTR, useExportSignal } from 'features/pdfExport';
import {
  FIRM_SUMMARY_CONTAINER_CLASSNAME,
  FIRM_SUMMARY_KEY,
  FIRM_SUMMARY_SPREADSHEET_ALL_FUNDS,
  FIRM_SUMMARY_TITLE,
  FIRM_SUMMARY_VALUES_SHOWN_PUBLISHED_TITLE,
} from 'pages/Firms/common/constants';
import { FirmSummaryAsyncDataProvider } from 'pages/Firms/components';
import { useFirmMeasurementDateFilter } from 'pages/Firms/hooks/useFirmMeasurementDateFilter';
import { FirmSummarySpreadsheet, useFirmSummarySheet } from 'pages/Firms/hooks/useFirmSummarySheet';
import {
  getOrderedColumnTypes,
  getRenderedFirmSummaryColumnTypes,
} from 'pages/Firms/hooks/useFirmSummarySheet/config/createColumns';
import FilterChipsBar from 'pages/Firms/pages/FirmSummary/FilterChipsBar';
import SortChipsBar from 'pages/Firms/pages/FirmSummary/SortChipsBar';
import { InformationMessage, TaskProgressDialog } from 'pages/Funds/components';
import type { ProcessManagementTaskDialogData } from 'pages/Funds/components/ProcessManagement';
import { useIntelligenceContext } from 'pages/Intelligence/IntelligenceContext';
import { PdfExportTargets } from 'pages/Valuations/util/constants';
import {
  useGetFirmChartData,
  useGetFirmSummary,
  useGetFirmSummaryData,
  useGetFirmValuationsChartData,
  useGetFirmValuationsData,
  useGetTasksProgressByFirm,
} from 'services/hooks/firms';
import { getArrayValue, getObjectValue, isValidDate } from 'utilities';
import { dbShortDate, ISO_DATE_FORMAT } from 'utilities/dateFormats';
import normalizeDefinitionRows from './normalizeDefinitionRows';
import { FundByCompany, UseFirmSummaryTabParams } from './useFirmSummaryTab.types';

const useFirmSummaryTab = (params: UseFirmSummaryTabParams) => {
  const {
    spreadsheetDefinition,
    definitionId,
    onSortToggle,
    valueTypeById,
    onClearFilters,
    summaryType,
    onRemoveFilter,
    onClearSorts,
    onRemoveSort,
    onAddColumn,
  } = params ?? {};
  const [storeValue] = useStore() as UseStoreValues;
  const { companyList: currentCompanies, firmInfo: currentFirm } = getObjectValue(storeValue);
  const { id: currentFirmId } = getObjectValue(currentFirm);
  const [selectedProcessManagementTask, setSelectedProcessManagementTask] =
    useState<ProcessManagementTaskDialogData | null>(null);
  const closeProcessManagementTaskDialog = useCallback(() => {
    setSelectedProcessManagementTask(null);
  }, []);

  const isValuations = summaryType === 'valuations';
  const isProcessManagementEnabled =
    isValuations && Boolean(getObjectValue(currentFirm).show_process_management_column);
  const isEnhancedMode = !isNil(definitionId);

  const { activeColumnWidths, setColumnWidth, setColumnOrder } = useSummaryDefinitions();

  const orderedColumnTypes = useMemo(() => getOrderedColumnTypes(spreadsheetDefinition), [spreadsheetDefinition]);

  const renderedColumnTypes = useMemo(
    () => getRenderedFirmSummaryColumnTypes(orderedColumnTypes, isProcessManagementEnabled),
    [isProcessManagementEnabled, orderedColumnTypes]
  );

  const handleColumnReorder = useCallback(
    async (sourceColumn: string, targetColumn: string) => {
      const sourceIdx = orderedColumnTypes.indexOf(sourceColumn);
      const targetIdx = orderedColumnTypes.indexOf(targetColumn);
      if (sourceIdx === -1 || targetIdx === -1 || sourceIdx === targetIdx) {
        return;
      }
      const newOrder = arrayMove(orderedColumnTypes, sourceIdx, targetIdx);

      // getOrderedColumnTypes filters out columns like company_name (handled separately by row headers).
      // Re-inject them at the front in their original sort order so the backend definition retains them.
      const orderedSet = new Set(orderedColumnTypes);
      const preservedColumns = Object.entries(spreadsheetDefinition?.columns ?? {})
        .sort(([a], [b]) => Number(a) - Number(b))
        .map(([, col]) => col)
        .filter(col => !orderedSet.has(col));

      const fullOrder = [...preservedColumns, ...newOrder];
      const columns = Object.fromEntries(fullOrder.map((col, idx) => [idx, col]));
      await setColumnOrder(columns);
    },
    [orderedColumnTypes, setColumnOrder, spreadsheetDefinition]
  );

  const handleColumnResize = useCallback(
    (column: string, width: number) => {
      setColumnWidth(column, width);
    },
    [setColumnWidth]
  );

  // Handle measurement date filter, query param, and context synchronization
  const {
    hasNoMeasurementDates,
    isMeasurementDateResolved,
    isMostRecentMeasurementDate,
    mostRecentMeasurementDate,
    selectedMeasurementDate,
  } = useFirmMeasurementDateFilter();

  const resolvedMeasurementDate =
    !isNil(selectedMeasurementDate) && isValidDate(selectedMeasurementDate.date)
      ? selectedMeasurementDate.date
      : undefined;

  const resolvedMeasurementDateForEnhanced = isMostRecentMeasurementDate ? undefined : resolvedMeasurementDate;

  const { isExportMode } = useLayoutContext();

  // Firm-level Gross IRR for the totals row (SCAL-9410). Kept as its own request so the
  // grid is never gated on it: the summary endpoint walks cash-flow ledger rows and can
  // take seconds, and the totals cell simply fills in once it resolves. The response
  // returns firm_gross_irr plus funds_gross_irr keyed by fund NAME (which is what
  // selectedFund holds), so one unfiltered request covers both the All Funds and
  // per-fund selections and the fund selection picks the right entry. Same date semantics
  // as the definition-data query: undefined = latest per company (Most Recent).
  // The value reaches its cell through FirmSummaryAsyncDataProvider rather than the parser,
  // so a late response fills one cell instead of rebuilding the sheet.
  const hasGrossIRRColumn = orderedColumnTypes.includes(COLUMN_TYPES.GROSS_IRR);
  const { data: firmSummary, isLoading: isLoadingFirmSummary } = useGetFirmSummary({
    firmId: currentFirmId,
    measurementDate: resolvedMeasurementDateForEnhanced,
    shouldQueryAutomatically:
      isEnhancedMode && hasGrossIRRColumn && !isNil(currentFirmId) && isMeasurementDateResolved,
  });
  const { firm_gross_irr: firmGrossIRR, funds_gross_irr: fundsGrossIRR } = getObjectValue(firmSummary);

  const processManagementMeasurementDate = isMostRecentMeasurementDate ? mostRecentMeasurementDate : selectedMeasurementDate;
  const processManagementMeasurementDateName = useMemo(
    () =>
      dbShortDate(processManagementMeasurementDate?.name ?? processManagementMeasurementDate?.date, ISO_DATE_FORMAT) ??
      '',
    [processManagementMeasurementDate?.date, processManagementMeasurementDate?.name]
  );

  const { data: tasksProgress, isLoading: isLoadingTasksProgress } = useGetTasksProgressByFirm({
    firm: currentFirm,
    shouldQueryAutomatically: isProcessManagementEnabled,
  });

  // Fund selection lives in the Intelligence secondary navigation (sticky via ?fund=).
  // The context owns the firm-funds list query so the secondary-nav dropdown and the
  // in-spreadsheet FundSelector share the same source of truth.
  const {
    fundOptions,
    isAllFundsSelected,
    isFundSelectionPending,
    selectedFund,
    selectedFundId,
    setSelectedFund: setSelectedFundFromContext,
    format,
    formatDispatch,
  } = useIntelligenceContext();

  // The in-spreadsheet FundSelector wants display names; the context's setter takes a
  // fund id (or the All Funds sentinel). Adapt name -> id so both selectors share state.
  const fundsAvailable = useMemo(
    () => [FIRM_SUMMARY_SPREADSHEET_ALL_FUNDS, ...fundOptions.map(fund => fund.name)],
    [fundOptions]
  );

  const fundIdByName = useMemo<Map<string, number>>(() => {
    const map = new Map<string, number>();
    fundOptions.forEach(fund => {
      if (fund?.name && typeof fund.id === 'number' && !map.has(fund.name)) {
        map.set(fund.name, fund.id);
      }
    });
    return map;
  }, [fundOptions]);

  const setSelectedFund = useCallback(
    (fundName: string) => {
      if (fundName === FIRM_SUMMARY_SPREADSHEET_ALL_FUNDS) {
        setSelectedFundFromContext(FIRM_SUMMARY_SPREADSHEET_ALL_FUNDS);
        return;
      }
      const fundId = fundIdByName.get(fundName);
      if (typeof fundId === 'number') {
        setSelectedFundFromContext(String(fundId));
      }
    },
    [fundIdByName, setSelectedFundFromContext]
  );

  const fundSelectionContextValue = useMemo(
    () => ({ fundsAvailable, selectedFund, setSelectedFund }),
    [fundsAvailable, selectedFund, setSelectedFund]
  );

  // Only the two filters that scope the request are waited on, and only while they are
  // genuinely ambiguous - a sticky ?date=/?fund= param that still needs its (slow) list to
  // resolve. With a bare URL both answers are known on the first render, so the definition
  // data request goes out in parallel with the measurement-dates and funds requests instead
  // of after them.
  const enhancedDataGate =
    isEnhancedMode &&
    !isNil(currentFirmId) &&
    !isNil(definitionId) &&
    isMeasurementDateResolved &&
    !isFundSelectionPending &&
    (isAllFundsSelected || !isNil(selectedFundId));

  const {
    data: firmDefinitionData,
    isFetched: isFetchedFirmDefinitionData,
    isFetching: isFetchingFirmDefinitionData,
    isLoading: isLoadingFirmDefinitionData,
  } = useGetFirmSummaryData({
    firmId: currentFirmId,
    definitionId,
    measurementDate: resolvedMeasurementDateForEnhanced,
    fundId: isEnhancedMode ? selectedFundId : undefined,
    shouldQueryAutomatically: !isValuations && enhancedDataGate,
  });

  const {
    data: valuationsDefinitionData,
    isFetched: isFetchedValuationsDefinitionData,
    isFetching: isFetchingValuationsDefinitionData,
    isLoading: isLoadingValuationsDefinitionData,
  } = useGetFirmValuationsData({
    firmId: currentFirmId,
    definitionId,
    measurementDate: resolvedMeasurementDateForEnhanced,
    fundId: isEnhancedMode ? selectedFundId : undefined,
    shouldQueryAutomatically: isValuations && enhancedDataGate,
  });

  const definitionData = isValuations ? valuationsDefinitionData : firmDefinitionData;
  const isFetchedDefinitionData = isValuations ? isFetchedValuationsDefinitionData : isFetchedFirmDefinitionData;
  const isFetchingDefinitionData = isValuations ? isFetchingValuationsDefinitionData : isFetchingFirmDefinitionData;
  const isLoadingDefinitionData = isValuations ? isLoadingValuationsDefinitionData : isLoadingFirmDefinitionData;

  const chartDataGate =
    isEnhancedMode &&
    !isNil(currentFirmId) &&
    !isNil(definitionId) &&
    isFetchedDefinitionData &&
    (isAllFundsSelected || !isNil(selectedFundId));

  // Prefetch chart data after spreadsheet data loads (non-blocking).
  // Scope to the selected fund so the popover trend matches the fund-filtered
  // table row; wait for the fund map (same gate as the definition data hook) to
  // avoid briefly firing a firm-wide chart request before the fund resolves.
  const { data: firmChartData, isLoading: isLoadingFirmChartData } = useGetFirmChartData({
    firmId: currentFirmId,
    definitionId,
    fundId: isEnhancedMode ? selectedFundId : undefined,
    shouldQueryAutomatically:
      !isValuations &&
      !isNil(currentFirmId) &&
      !isNil(definitionId) &&
      isFetchedDefinitionData &&
      (isAllFundsSelected || !isNil(selectedFundId)),
  });

  const { data: valuationsChartData, isLoading: isLoadingValuationsChartData } = useGetFirmValuationsChartData({
    firmId: currentFirmId,
    definitionId,
    fundId: isEnhancedMode ? selectedFundId : undefined,
    shouldQueryAutomatically: isValuations && chartDataGate,
  });

  const chartData = isValuations ? valuationsChartData : firmChartData;
  const isLoadingChartData = isValuations ? isLoadingValuationsChartData : isLoadingFirmChartData;

  const chartDataContextValue = useMemo(
    () => ({ chartData, isLoading: isLoadingChartData, selectedDate: resolvedMeasurementDateForEnhanced }),
    [chartData, isLoadingChartData, resolvedMeasurementDateForEnhanced]
  );

  // funds_gross_irr is keyed by fund NAME, which is exactly what selectedFund holds, so the
  // single unfiltered response serves both the All Funds and per-fund selections.
  const grossIRRContextValue = useMemo(
    () => ({
      grossIRR: isAllFundsSelected ? firmGrossIRR : fundsGrossIRR?.[selectedFund],
      isLoading: hasGrossIRRColumn && isLoadingFirmSummary,
    }),
    [firmGrossIRR, fundsGrossIRR, hasGrossIRRColumn, isAllFundsSelected, isLoadingFirmSummary, selectedFund]
  );

  // TaskCell matches tasks by measurement date name, so the cell stays in its loading state
  // until the date itself is known - neither is worth blocking the grid for. Gate on date
  // resolution rather than isLoadingMeasurementDates, which includes isFetching and would
  // keep Process Management cells (and PDF export) spinning through a background refetch.
  const tasksProgressContextValue = useMemo(
    () => ({
      tasksProgress,
      measurementDateName: processManagementMeasurementDateName,
      isLoading: Boolean(isProcessManagementEnabled) && (isLoadingTasksProgress || !isMeasurementDateResolved),
    }),
    [
      isLoadingTasksProgress,
      isMeasurementDateResolved,
      isProcessManagementEnabled,
      processManagementMeasurementDateName,
      tasksProgress,
    ]
  );

  const groupBy: SummaryGroupBy = useMemo(() => spreadsheetDefinition?.groupBy ?? 'company', [spreadsheetDefinition]);

  const sortKeys = useMemo<SortKey[]>(() => spreadsheetDefinition?.sortKeys ?? [], [spreadsheetDefinition]);
  const filters = useMemo<Filter[]>(() => spreadsheetDefinition?.filters ?? [], [spreadsheetDefinition]);

  // Backend returns rows already scoped to the selected fund (or firm-wide for All Funds)
  // and sorted per the definition, so no client-side filtering/sorting is needed.
  const orderedFundsByCompany = useMemo<FundByCompany[]>(
    () => normalizeDefinitionRows(definitionData?.rows ?? [], resolvedMeasurementDateForEnhanced),
    [definitionData, resolvedMeasurementDateForEnhanced]
  );

  const hasDraftFunds = useMemo<boolean>(
    () => orderedFundsByCompany.some(fund => !fund.is_published && !fund.is_final),
    [orderedFundsByCompany]
  );

  const hasNoCompanies = useMemo<boolean>(
    () => !isNil(currentCompanies) && isEmpty(currentCompanies),
    [currentCompanies]
  );

  const hasNoInvestments = useMemo<boolean>(
    () =>
      hasNoMeasurementDates ||
      (Boolean(isFetchedDefinitionData) &&
        (isNil(definitionData) || isEmpty(definitionData?.rows)) &&
        isEmpty(orderedFundsByCompany)),
    [definitionData, hasNoMeasurementDates, isFetchedDefinitionData, orderedFundsByCompany]
  );

  // The skeleton tracks the request that produces rows plus the filter resolution that
  // request is gated on - nothing else. The totals Gross IRR, the Process Management tasks
  // and the fund list each resolve into their own cell, so none of them holds the grid back.
  // The fund term stays because the definition-data query is disabled while a ?fund= param
  // is unresolved (see enhancedDataGate), which would otherwise blank the grid rather than
  // keep the skeleton up.
  const isLoading = useMemo<boolean>(
    () => isLoadingDefinitionData || !isMeasurementDateResolved || (isEnhancedMode && isFundSelectionPending),
    [isEnhancedMode, isFundSelectionPending, isLoadingDefinitionData, isMeasurementDateResolved]
  );

  // PDF export snapshots the rendered grid, so it has to wait for the async cells as well:
  // they are allowed to fill in on screen, but must not be captured as placeholders.
  const isLoadingAsyncCells = grossIRRContextValue.isLoading || tasksProgressContextValue.isLoading;

  let firmSummaryExportStatus: 'loading' | 'missing' | 'ready' = 'ready';
  if (isLoading || isLoadingAsyncCells) {
    firmSummaryExportStatus = 'loading';
  } else if (hasNoCompanies || hasNoInvestments) {
    firmSummaryExportStatus = 'missing';
  }
  useExportSignal({
    target: PdfExportTargets.FIRM_SUMMARY.id,
    status: firmSummaryExportStatus,
  });

  // Spreadsheet

  const rowGroupParentKeys = useMemo<string[]>(
    () =>
      uniq(
        orderedFundsByCompany
          .map(item => getObjectValue(item).parent_key)
          .filter(Boolean)
          .map(key => toString(key))
      ),
    [orderedFundsByCompany]
  );

  const rowGroupsInitialState = useMemo<RowsGroups>(
    () =>
      Object.fromEntries(rowGroupParentKeys.map(key => [key, isAllFundsSelected ? SYSTEM_COLLAPSE : SYSTEM_EXPAND])),
    [isAllFundsSelected, rowGroupParentKeys]
  );

  const [rowGroups, setRowGroups] = useReducer(rowsGroupsReducer, rowGroupsInitialState);

  useEffect(() => {
    if (rowGroupParentKeys.length > 0) {
      setRowGroups({
        type: RESET,
        payload: Object.fromEntries(
          rowGroupParentKeys.map(key => [key, isAllFundsSelected ? SYSTEM_COLLAPSE : SYSTEM_EXPAND])
        ),
      });
    }
  }, [rowGroupParentKeys, isAllFundsSelected]);

  const { spreadsheet: firmSummarySpreadsheet } = useFirmSummarySheet({
    fundsByCompany: orderedFundsByCompany,
    groupBy,
    isAllFundsSelected,
    spreadsheetDefinition,
    isProcessManagementEnabled,
    onSortToggle,
    onProcessManagementTaskClick: setSelectedProcessManagementTask,
  });

  const spreadsheets = useMemo<FirmSummarySpreadsheet[]>(
    () =>
      // eslint-disable-next-line @typescript-eslint/ban-ts-comment
      // @ts-ignore: private 'yLength' property
      firmSummarySpreadsheet?.yLength ? [firmSummarySpreadsheet] : [],
    [firmSummarySpreadsheet]
  );

  // eslint-disable-next-line @typescript-eslint/ban-ts-comment
  // @ts-ignore: private 'shouldValidate' property
  const { onChange, workbook, workbookContextValue } = useWorkbook(spreadsheets);

  const firmSummarySpreadsheetProps = useMemo<ScalarSpreadsheetProps | null>(() => {
    if (firmSummarySpreadsheet) {
      return {
        ...firmSummarySpreadsheet,
        data: getArrayValue(firmSummarySpreadsheet?.data),
        format,
        formatDispatch,
        onChange,
        sheet: firmSummarySpreadsheet,
        workbook,
        rowGroups,
        setRowGroups,
        columnWidths: activeColumnWidths,
        onColumnResize: handleColumnResize,
        columnTypes: renderedColumnTypes,
        onColumnReorder: handleColumnReorder,
        // ScalarSpreadsheet appends a whole "Add Column +" column when this is set; keep it out
        // of the PDF capture.
        onAddColumn: isExportMode ? undefined : onAddColumn,
      } as unknown as ScalarSpreadsheetProps;
    }

    return null;
  }, [
    firmSummarySpreadsheet,
    format,
    formatDispatch,
    onChange,
    rowGroups,
    workbook,
    activeColumnWidths,
    handleColumnResize,
    renderedColumnTypes,
    handleColumnReorder,
    onAddColumn,
    isExportMode,
  ]);

  // The fund column lists every fund a company sits in and wraps into very tall rows; the PDF
  // capture may widen it so a long summary fits the page larger (see fitExportColumnsToPage).
  const exportFitColumnLegend = useMemo(() => {
    const fundColumnIndex = renderedColumnTypes.indexOf(COLUMN_TYPES.FUND_NAME);
    return fundColumnIndex === -1 ? undefined : firmSummarySpreadsheet?.alphabet?.[fundColumnIndex];
  }, [firmSummarySpreadsheet, renderedColumnTypes]);

  const hasExpandedRowGroup = useMemo(
    () => Object.values(rowGroups).some(rowGroup => [SYSTEM_EXPAND, USER_EXPAND].includes(rowGroup)),
    [rowGroups]
  );

  const firmSummarySpreadsheetContent = useMemo<JSX.Element | null>(() => {
    if (hasNoCompanies) {
      return null;
    }

    // Filters reduced the result set to zero rows - let the user know via chips bar
    const isFilteredEmpty = filters.length > 0 && hasNoInvestments;

    if (hasNoInvestments && !isFilteredEmpty) {
      return null;
    }

    const sortChipsBar =
      sortKeys.length > 0 && !isExportMode ? (
        <SortChipsBar
          sortKeys={sortKeys}
          valueTypeById={valueTypeById ?? new Map()}
          onClearAll={onClearSorts ?? (() => Promise.resolve())}
          onRemoveSort={onRemoveSort ?? (() => Promise.resolve())}
        />
      ) : null;

    const filterChipsBar =
      filters.length > 0 && !isExportMode ? (
        <FilterChipsBar
          filters={filters}
          valueTypeById={valueTypeById ?? new Map()}
          onClearAll={onClearFilters ?? (() => Promise.resolve())}
          onRemoveFilter={onRemoveFilter ?? (() => Promise.resolve())}
        />
      ) : null;

    let gridContent: JSX.Element | null | false = null;
    if (isLoading) {
      gridContent = <GridSkeleton maxColumns={12} maxRows={4} />;
    } else if (isFilteredEmpty) {
      gridContent = (
        <Box>
          {sortChipsBar}
          {filterChipsBar}
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
            <Typography variant="body2" color="text.secondary">
              No results match the active filters.
            </Typography>
          </Box>
        </Box>
      );
    } else if (firmSummarySpreadsheetProps && firmSummarySpreadsheet) {
      gridContent = (
        <FirmSummaryAsyncDataProvider
          fundSelection={fundSelectionContextValue}
          grossIRR={grossIRRContextValue}
          tasksProgress={tasksProgressContextValue}>
          <ChartDataContext.Provider value={chartDataContextValue}>
            <WorkbookContext.Provider value={workbookContextValue}>
              <CustomCompactContainer>
                <Grid
                  className={clsx(FIRM_SUMMARY_CONTAINER_CLASSNAME, { expanded: hasExpandedRowGroup })}
                  size={{ xs: 12 }}>
                  {sortChipsBar}
                  {filterChipsBar}
                  {/* Only the rows request dims the grid: the async cells show their own
                      placeholders rather than greying out the whole sheet. */}
                  <CustomScalarSpreadsheet {...firmSummarySpreadsheetProps} isLoading={isFetchingDefinitionData} />
                </Grid>

                {!isExportMode && (
                  <InformationMessage
                    displayMessage={hasDraftFunds}
                    message={FIRM_SUMMARY_VALUES_SHOWN_PUBLISHED_TITLE}
                  />
                )}
              </CustomCompactContainer>
            </WorkbookContext.Provider>
          </ChartDataContext.Provider>
        </FirmSummaryAsyncDataProvider>
      );
    }

    return (
      <CustomContainer
        data-ref={PdfExportTargets.FIRM_SUMMARY.id}
        {...(exportFitColumnLegend ? { [EXPORT_FIT_COLUMNS_ATTR]: exportFitColumnLegend } : {})}>
        <Grid className={FIRM_SUMMARY_CONTAINER_CLASSNAME} size={{ xs: 12 }}>
          {gridContent}
        </Grid>
      </CustomContainer>
    );
  }, [
    chartDataContextValue,
    exportFitColumnLegend,
    filters,
    firmSummarySpreadsheet,
    firmSummarySpreadsheetProps,
    fundSelectionContextValue,
    grossIRRContextValue,
    hasDraftFunds,
    hasExpandedRowGroup,
    hasNoCompanies,
    hasNoInvestments,
    isFetchingDefinitionData,
    isExportMode,
    isLoading,
    onClearFilters,
    onClearSorts,
    onRemoveFilter,
    onRemoveSort,
    sortKeys,
    tasksProgressContextValue,
    valueTypeById,
    workbookContextValue,
  ]);

  const firmSummaryTabContent = useMemo<JSX.Element | null>(() => {
    if (!firmSummarySpreadsheetContent) {
      return null;
    }

    return (
      <>
        {firmSummarySpreadsheetContent}
        {selectedProcessManagementTask && (
          <TaskProgressDialog
            {...selectedProcessManagementTask}
            open
            onClose={closeProcessManagementTaskDialog}
            firmName={toString(getObjectValue(currentFirm).name)}
          />
        )}
      </>
    );
  }, [closeProcessManagementTaskDialog, currentFirm, firmSummarySpreadsheetContent, selectedProcessManagementTask]);

  // Tab
  const firmSummaryTab = useMemo<Tab>(
    () =>
      ({
        id: FIRM_SUMMARY_KEY,
        label: FIRM_SUMMARY_TITLE,
        content: firmSummaryTabContent,
      }) as Tab,
    [firmSummaryTabContent]
  );

  const isFilteredEmpty = filters.length > 0 && hasNoInvestments;

  return { firmSummaryTab, hasNoCompanies, hasNoInvestments, isFilteredEmpty };
};

export default useFirmSummaryTab;
